import { create } from 'zustand'
import { api, ApiRequestError } from '../lib/api'
import { clearReturningVisitorFlag } from '../lib/returningVisitor'
import { useCVGenerationStore } from './cvGenerationStore'

/** Which UI shell a user sees (redesign F2). */
export type UiVersion = 'old' | 'new'

export interface CurrentUser {
  id: string
  email: string
  displayName: string | null
  isAdmin: boolean
  apiKeyMode: string | null
  accountStatus: string
  onboardingCompleted: boolean
  uiVersion: UiVersion
}

interface UserMeResponse {
  id: string
  email: string
  is_admin: boolean
  api_key_mode: string | null
  account_status: string
  onboarding_completed: boolean
  // Optional: a backend deployed before F2 doesn't send them.
  ui_version?: string
  display_name?: string | null
}

/** Anything but an explicit 'new' is the old UI, so an older backend keeps everyone on old. */
export function toUiVersion(value: string | undefined): UiVersion {
  return value === 'new' ? 'new' : 'old'
}

type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated'

interface AuthState {
  user: CurrentUser | null
  status: AuthStatus
  fetchUser: () => Promise<void>
  signOut: () => Promise<void>
  clear: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'unknown',

  fetchUser: async () => {
    try {
      const data = await api.get<UserMeResponse>('/api/v1/user/me')
      set({
        user: {
          id: data.id,
          email: data.email,
          displayName: data.display_name ?? null,
          isAdmin: data.is_admin,
          apiKeyMode: data.api_key_mode,
          accountStatus: data.account_status,
          onboardingCompleted: data.onboarding_completed,
          uiVersion: toUiVersion(data.ui_version),
        },
        status: 'authenticated',
      })
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        set({ user: null, status: 'unauthenticated' })
      } else {
        // Network error or unexpected — treat as unauthenticated
        set({ user: null, status: 'unauthenticated' })
      }
    }
  },

  signOut: async () => {
    try {
      await api.post('/auth/signout')
    } finally {
      clearReturningVisitorFlag()
      useCVGenerationStore.getState().reset()
      set({ user: null, status: 'unauthenticated' })
    }
  },

  clear: () => set({ user: null, status: 'unauthenticated' }),
}))
